import {
  attr,
  customElement,
  FASTElement,
  Observable,
  observable,
  Splice,
} from '@microsoft/fast-element';
import { UserModalTemplate as template } from './user-modal.template';
import { UserModalStyles as styles } from './user-modal.styles';
import { RightSideModal } from '../../right-side-modal/right-side-modal';
import { ValidationColumnConfig } from '../../common/validation-grid/validation-grid';
import { IRightMenuService } from '../../../services/right-menu.service';
import { IUserCompany } from '../../../types';
import { Auth, logger, Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { ICompanyService } from '../../../services/company.service';
import { Select } from '@genesislcap/foundation-ui';
import { PROFILE_RIGHTS } from '../../../utils';
import { notifyError } from '../../../utils/notify';
import { IAuthService } from '../../../services/auth.service';
import { IUserService } from '../../../services/user.service';
import { INotificationService } from '../../../services/notification.service';
import { EditCompany } from '../../mf/companys/edit/edit';

const name = 'user-modal';

export enum Labels {
  TITLE = 'User Settings',
}

@customElement({
  name,
  template,
  styles,
})
export class UserModal extends FASTElement {
  @observable showModal: boolean = false;
  @observable userModal: RightSideModal;
  @observable userColumns: Array<ValidationColumnConfig> = [];
  @observable isUpdateModal: boolean = false;
  @observable userCompanies: IUserCompany[] = [] as IUserCompany[];
  @observable userCompanyElement: Select;
  @observable currentSideElement: Select;
  @observable currentCompanyId: string;
  @observable currentSide: string;
  @INotificationService notificationService: INotificationService;
  @IRightMenuService rightMenuService: IRightMenuService;
  @ICompanyService companyService: ICompanyService;
  @IAuthService authService: IAuthService;
  @observable show: boolean = false;
  @observable title = Labels.TITLE;
  @observable width: string = '662px';
  @attr username: String;
  @attr profiles: String;
  @Auth auth: Auth;
  @IUserService userService: IUserService;

  @observable editCompanyModal: EditCompany;
  @observable autoExecuteApprovedCounterpartiesModal: boolean = false;

  @observable editEntityModal: any;
  @observable isEditable = false;
  @observable isAdvisor = false

  private userCompanySubscription: SocketSubscription;

  private notifier: any;
  private handler: any;

  connectedCallback() {
    super.connectedCallback();
    this.loadInfo();
    this.showModalListener();
    this.streamUserCompanies();
    setTimeout(() => {
         this.isAdvisor = this.setCompanyAdmin()
        }, 500)
  }

  private showModalListener() {
    const _this = this;
    this.notifier = Observable.getNotifier(this.rightMenuService);
    this.handler = {
      handleChange(source: any, splices: Splice[]) {
        _this.show = source.userModalVisibility;
      },
    };
    this.notifier.subscribe(this.handler, 'userModalVisibility');
  }

  private loadInfo() {
    this.username = this.auth.loggedUserResult.username;
    this.profiles = this.auth.loggedUserResult.profiles.join(', ');
    this.currentSide = this.userService.getCurrentSide();
    this.userCompanyElement.value = this.currentSide;
  }

  public closeModal() {
    this.rightMenuService.closeUserModal();
  }

  public async logout() {
    this.authService.logout();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.userCompanySubscription.unsubscribe();
    this.rightMenuService.closeUserModal();
  }

  public companySelected() {
    sessionStorage.removeItem('criteria')
    this.companyService.updateCurrentCompany(this.currentCompanyId).then(
      async (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
          await this.companyService.setCompany();
          this.notificationService.redirectToDashboard()
          location.reload();
        } else {
          this.currentCompanyId = this.companyService.getCurrentCompany().CASCADE_COMPANY_ID;
          notifyError(response.ERROR[0].TEXT);
          logger.error(response.ERROR);
        }
      },
      (error) => {
        this.currentCompanyId = this.companyService.getCurrentCompany().CASCADE_COMPANY_ID;
        notifyError(error);
        logger.error(error);
      },
    );
  }

  public sideSelected() {
    this.userService.eventUpdateSide(this.currentSide).then(
      (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
          location.reload();
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      },
    );
  }

  private streamUserCompanies() {
    let resultList = [];
    this.userCompanySubscription?.unsubscribe();

    this.userCompanySubscription = this.companyService.getAllUserCompanies().subscribe({
      next: (streamMessage: Message) => {
        resultList = streamMessage.ROW ?? null;
        if (resultList) {
          this.userCompanies = this.updateCompanyArray(resultList, this.userCompanies);
          this.userCompanyElement.value = this.currentCompanyId;
        } else {
          this.userCompanies = [];
          const resultERROR = streamMessage.DETAILS?.ERROR;
          logger.error(resultERROR);
        }
      },
      error: (error: any) => {
        this.userCompanies = [];
        logger.error(error);
      },
    });
    setTimeout(() => {
      this.currentCompanyId = this.companyService.getCurrentCompany().CASCADE_COMPANY_ID;
    }, 200);
  }

  updateCompanyArray(newData: Array<any>, oldData: Array<any>): Array<any> {
    if (newData && oldData && oldData.length > 0) {
      newData.forEach((row) => {
        const eventData = row;

        if (eventData && eventData.DETAILS) {
          const ref = eventData.DETAILS.ROW_REF;
          const type = eventData.DETAILS.OPERATION;

          if (type === 'INSERT') {
            if (!oldData.find((x) => x.DETAILS?.ROW_REF === ref)) {
              oldData.push(eventData);
            }
          }

          if (type === 'DELETE') {
            const index = oldData.findIndex((x) => row.DETAILS?.ROW_REF === x.DETAILS?.ROW_REF);
            const oldValue = oldData.find((x) => row.DETAILS?.ROW_REF === x.DETAILS?.ROW_REF);
            if (index != undefined) {
              oldData.splice(index, 1);
              if (oldValue.CASCADE_COMPANY_ID === this.currentCompanyId) {
                location.reload();
              }
            }
          }

          if (type === 'MODIFY') {
            oldData = oldData.map((item: any) =>
              item.DETAILS?.ROW_REF === ref ? { ...item, ...eventData } : item,
            );
          }
        }
      });
    } else if (newData && (!oldData || oldData.length === 0)) {
      oldData = [...new Set(newData)] as any[];
    }
    return oldData;
  }

  openCompanyModal() {
    this.editModalVisible = true;
    setTimeout(() => {
      this.editCompanyModal.editEntity({ detail: this.companyService.getCurrentCompany() });
    }, 100);
  }

  @observable editModalVisible: boolean = false;
  editModalVisibleChanged() {
    if (this.editEntityModal) {
      if (this.editModalVisible) {
        this.editEntityModal.show();
      } else {
        this.editEntityModal.close();
      }
    }
  }

  public closeCompanyModal() {
    this.editModalVisible = false;
  }

  setCompanyAdmin(): boolean {
    return (
        this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.ADMIN_VISIBILITY) ||
        (
            this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.COMPANY_MANAGEMENT) &&
            (!this.userService.hasAdvisorRights() || (this.userService.getCurrentUser().COMPANY_ID == this.currentCompanyId))
        )
    ) ;
  }
}
